import { AnalysisResult } from "../analyze/models.js";
import { OrchestratorConfig } from "../config/configTypes.js";

export type ThresholdOutcome = "PASS" | "FAIL" | "WARNING_LIMIT";

export interface ThresholdEvaluation {
  outcome: ThresholdOutcome;
  /** Human-readable reason for the outcome */
  reason: string;
}

/**
 * Evaluates an analysis result against the configured thresholds.
 *
 * Evaluation order:
 *  1. Error presence (when failOnError is true) → FAIL
 *  2. Score below threshold → FAIL
 *  3. Warning count above maxWarnings → WARNING_LIMIT
 *  4. Otherwise → PASS
 */
export function evaluateThresholds(
  result: AnalysisResult,
  config: OrchestratorConfig
): ThresholdEvaluation {
  // 1. Check for errors when failOnError is set
  if (config.failOnError && result.errors.length > 0) {
    return {
      outcome: "FAIL",
      reason: `${result.errors.length} critical error(s) found (failOnError: true).`,
    };
  }

  // 2. Score threshold check
  if (result.score < config.scoreThreshold) {
    return {
      outcome: "FAIL",
      reason: `Health score ${result.score}/100 is below threshold of ${config.scoreThreshold}.`,
    };
  }

  // 3. Warning count threshold
  if (result.warnings.length > config.maxWarnings) {
    return {
      outcome: "WARNING_LIMIT",
      reason: `${result.warnings.length} warnings exceed the maxWarnings threshold of ${config.maxWarnings}.`,
    };
  }

  return {
    outcome: "PASS",
    reason: `Score ${result.score}/100 meets threshold. No blocking issues found.`,
  };
}
